import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { Check, Minus } from "@phosphor-icons/react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type CheckboxProps = RadixCheckbox.CheckboxProps & {
  label?: ReactNode;
  description?: ReactNode;
  error?: boolean;
  size?: "sm" | "md" | "lg";
};

const indicatorSize = {
  sm: 9,
  md: 10,
  lg: 12
} as const;

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(({ className, label, description, size = "sm", error, ...props }, ref) => (
  <label className={cn("bfrs-inline-flex bfrs-items-start bfrs-gap-2 bfrs-text-[12px] bfrs-text-text-primary", props.disabled ? "bfrs-text-text-disabled" : "bfrs-cursor-pointer")}>
    <RadixCheckbox.Root
      ref={ref}
      aria-invalid={error}
      className={cn(
        "bfrs-mt-0.5 bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-sm bfrs-bg-surface-card bfrs-text-primary bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50 data-[state=checked]:bfrs-border-primary data-[state=checked]:bfrs-bg-primary data-[state=checked]:bfrs-text-primary-contrast data-[state=indeterminate]:bfrs-border-primary data-[state=indeterminate]:bfrs-bg-primary data-[state=indeterminate]:bfrs-text-primary-contrast",
        size === "sm" && "bfrs-size-3 bfrs-rounded-[2.5px] bfrs-border-[0.8px]",
        size === "md" && "bfrs-size-3.5 bfrs-rounded-[3px] bfrs-border",
        size === "lg" && "bfrs-size-4 bfrs-rounded-[4px] bfrs-border",
        error ? "bfrs-border-danger" : "bfrs-border-text-secondary",
        className
      )}
      {...props}
    >
      <RadixCheckbox.Indicator>
        {props.checked === "indeterminate" ? <Minus size={indicatorSize[size]} weight="bold" /> : <Check size={indicatorSize[size]} weight="bold" />}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
    {(label || description) && (
      <span className="bfrs-grid bfrs-gap-0.5">
        {label && <span className={cn("bfrs-font-medium", props.disabled ? "bfrs-text-text-disabled" : "bfrs-text-text-primary")}>{label}</span>}
        {description && <span className={cn("bfrs-text-xs bfrs-leading-5", props.disabled ? "bfrs-text-text-disabled" : "bfrs-text-text-muted")}>{description}</span>}
      </span>
    )}
  </label>
));

Checkbox.displayName = "Checkbox";
